import React, { useState } from 'react';
import { calculateHealthStatus } from '../../utils/healthStatus';
import { BowelMovement } from '../../types';

interface HealthStatusStickProps {
  recentMovements: BowelMovement[];
}

export const HealthStatusStick: React.FC<HealthStatusStickProps> = ({
  recentMovements,
}) => {
  const [showDetails, setShowDetails] = useState(false);
  const summary = calculateHealthStatus(recentMovements);

  const statusConfig = {
    good: {
      color: '#10b981',
      bg: '#d1fae5',
      text: '✓ Dobry dzień',
    },
    warning: {
      color: '#f59e0b',
      bg: '#fef3c7',
      text: '⚠ Obserwuj',
    },
    alert: {
      color: '#ef4444',
      bg: '#fee2e2',
      text: '⚠ Uwaga',
    },
  };

  const config = statusConfig[summary.status];

  return (
    <>
      <div
        onClick={() => setShowDetails(true)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 16px',
          borderRadius: '12px',
          background: config.bg,
          border: `1px solid ${config.color}`,
          cursor: 'pointer',
          transition: 'all 0.2s',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = `0 4px 12px ${config.color}30`;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = 'none';
        }}
      >
        <div
          style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            background: config.color,
          }}
        />
        <span
          style={{
            fontSize: '14px',
            fontWeight: '600',
            color: config.color,
          }}
        >
          {config.text}
        </span>
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke={config.color}
          strokeWidth="2"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="16" x2="12" y2="12" />
          <line x1="12" y1="8" x2="12.01" y2="8" />
        </svg>
      </div>

      {showDetails && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
          }}
          onClick={() => setShowDetails(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '20px',
              padding: '24px',
              maxWidth: '500px',
              width: '100%',
              maxHeight: '80vh',
              overflow: 'auto',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '20px',
              }}
            >
              {summary.status === 'good' && (
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    background: config.bg,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={config.color} strokeWidth="2.5">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>
              )}
              {summary.status === 'warning' && (
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    background: config.bg,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={config.color} strokeWidth="2.5">
                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                    <line x1="12" y1="9" x2="12" y2="13" />
                    <line x1="12" y1="17" x2="12.01" y2="17" />
                  </svg>
                </div>
              )}
              {summary.status === 'alert' && (
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    background: config.bg,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={config.color} strokeWidth="2.5">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                </div>
              )}
              <div>
                <h2
                  style={{
                    fontSize: '20px',
                    fontWeight: '700',
                    color: '#0f172a',
                    margin: '0 0 4px',
                  }}
                >
                  {summary.title}
                </h2>
                <p
                  style={{
                    fontSize: '13px',
                    color: '#64748b',
                    margin: 0,
                  }}
                >
                  Ostatnie 7 dni
                </p>
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              {summary.reasons.map((reason, index) => (
                <div
                  key={index}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '10px',
                    marginBottom: '10px',
                    padding: '10px',
                    background: '#f8fafc',
                    borderRadius: '8px',
                  }}
                >
                  <span style={{ fontSize: '16px' }}>
                    {summary.status === 'good' ? '✓' : '•'}
                  </span>
                  <span
                    style={{
                      fontSize: '14px',
                      color: '#0f172a',
                      lineHeight: '1.5',
                    }}
                  >
                    {reason}
                  </span>
                </div>
              ))}
            </div>

            <div
              style={{
                padding: '14px',
                background: config.bg,
                borderRadius: '12px',
                borderLeft: `4px solid ${config.color}`,
                marginBottom: '16px',
              }}
            >
              <p
                style={{
                  fontSize: '14px',
                  color: '#0f172a',
                  margin: 0,
                  fontWeight: '500',
                }}
              >
                {summary.recommendations}
              </p>
            </div>

            <button
              onClick={() => setShowDetails(false)}
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '10px',
                border: '2px solid #e2e8f0',
                background: 'white',
                fontSize: '15px',
                fontWeight: '600',
                color: '#64748b',
                cursor: 'pointer',
              }}
            >
              Zamknij
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export const SmartSummaryBadge = HealthStatusStick;
